import { openDB } from 'idb';

const DB_NAME = 'daily-doodle-timelapse';
const DB_VERSION = 1;
const STORE = 'frames';

let dbPromise = null;

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('by_session', ['guid', 'date']);
          store.createIndex('by_capturedAt', 'capturedAt');
        }
      },
    });
  }
  return dbPromise;
};

export const getTodayCST = () => {
  const parts = new Date()
    .toLocaleString('en-US', { timeZone: 'America/Chicago' })
    .split(',')[0]
    .split('/');
  const [m, d, y] = parts;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

export const putFrame = async ({ guid, date, frameIndex, blob, w, h }) => {
  const db = await getDB();
  const id = `${guid}|${date}|${frameIndex}`;
  const record = {
    id,
    guid,
    date,
    frameIndex,
    capturedAt: Date.now(),
    blob,
    w,
    h,
  };
  try {
    await db.put(STORE, record);
  } catch (err) {
    if (err && err.name === 'QuotaExceededError') {
      await dropOldestQuarterForSession(guid, date);
      try {
        await db.put(STORE, record);
      } catch {
        console.warn('[timelapse] storage quota still exceeded after pruning');
      }
    } else {
      throw err;
    }
  }
};

const dropOldestQuarterForSession = async (guid, date) => {
  const db = await getDB();
  const tx = db.transaction(STORE, 'readwrite');
  const idx = tx.store.index('by_session');
  const keys = await idx.getAllKeys(IDBKeyRange.only([guid, date]));
  const toDrop = Math.ceil(keys.length / 4);
  for (let i = 0; i < toDrop; i++) {
    await tx.store.delete(keys[i]);
  }
  await tx.done;
  console.warn(`[timelapse] dropped oldest ${toDrop} frames to free quota`);
};

export const listFrames = async (guid, date) => {
  const db = await getDB();
  const tx = db.transaction(STORE, 'readonly');
  const idx = tx.store.index('by_session');
  const all = await idx.getAll(IDBKeyRange.only([guid, date]));
  await tx.done;
  all.sort((a, b) => a.frameIndex - b.frameIndex);
  return all;
};

export const countFrames = async (guid, date) => {
  const db = await getDB();
  const idx = db.transaction(STORE, 'readonly').store.index('by_session');
  return idx.count(IDBKeyRange.only([guid, date]));
};

export const purgeSession = async (guid, date) => {
  const db = await getDB();
  const tx = db.transaction(STORE, 'readwrite');
  const idx = tx.store.index('by_session');
  const keys = await idx.getAllKeys(IDBKeyRange.only([guid, date]));
  for (const k of keys) await tx.store.delete(k);
  await tx.done;
};

export const purgeOlderThan = async (days) => {
  const db = await getDB();
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const tx = db.transaction(STORE, 'readwrite');
  const idx = tx.store.index('by_capturedAt');
  let cursor = await idx.openCursor(IDBKeyRange.upperBound(cutoff));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
};
